import React, { useEffect, useMemo, useCallback, useContext } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";

import { HookSelectFormGroup } from "components";
import { HighRiskContext } from "components/contexts";
import { ViewContainer } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { dropdownValuesService } from "services";
import { SplitProfileItem } from "types";
import { requiredDropDownSplitProfile } from "validators";

import SplitProfileOption from "./splitProfileOption";

import { ViewSplitProfile } from "./index";


import { ReactComponent as AlertIcon } from "static/images/alert-round.svg";
import styles from "../splitProfile.module.scss";

const defaultSplitProfile = {
	value: "",
	name: "Not assigned",
	isHighRisk: false,
	isAvailable: false,
};

const splitProfileListingOptions = {
	showHighRisk: true,
	showInactive: true,
	sortBy: "name",
	ascending: true,
};

const SplitProfileWarningMessage = ({ splitProfileItems, isLoading }) => {
	const noSplitProfileItems =
		!splitProfileItems ||
		splitProfileItems?.length <= 1 ||
		!splitProfileItems?.some(item => item.isAvailable);

	return noSplitProfileItems && !isLoading ? (
		<small className="text-warning">
			No Residual Split profile available on agent profile. Please contact
			Agent Relations to set one up to avoid any delay to residual
			payments.
		</small>
	) : null;
};

type SplitProfileViewSelectProps = {
	agentId: string;
	isEditable: boolean;
	canViewResidualsDetails: boolean;
	initialSplitProfile: string;
	isHighRisk?: boolean;
	isUnderwritingResidualsTab?: boolean;
	isResetSplitProfilePrevValue?: boolean;
	isLoadingData?: boolean;
	isRequired?: boolean;
	section?: "Merchant" | "Underwriting";
	setHandleSubmit?: Function;
	getHighRiskItem?: Function;
	onChange?: Function;
	prevValue?: SplitProfileItem;
	setPrevValue: Function;
	setIsMoreThanTwoHighRiskSplitProfiles?: Function;
	setIsResetSplitProfilePrevValue?: Function;
};

export const SplitProfileViewSelect = ({
	agentId,
	isEditable,
	canViewResidualsDetails,
	initialSplitProfile,
	isHighRisk,
	isResetSplitProfilePrevValue = false,
	isLoadingData,
	isRequired,
	section,
	setHandleSubmit,
	getHighRiskItem,
	onChange,
	prevValue,
	setPrevValue,
	setIsMoreThanTwoHighRiskSplitProfiles,
	setIsResetSplitProfilePrevValue,
}: SplitProfileViewSelectProps) => {
	const methods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
	});

	const { control, setValue, handleSubmit, reset } = methods;

	const splitProfileList = useWatch({
		control,
		name: "splitProfileList",
		defaultValue: defaultSplitProfile,
	});

	const {
		data: splitProfileItems,
		isFetching,
		isSuccess,
		error,
	} = useQuery(
		["splitProfileList", agentId, splitProfileListingOptions],
		() =>
			dropdownValuesService.getSplitProfiles(
				agentId,
				splitProfileListingOptions,
			),
		{
			select: (data): SplitProfileItem[] => [
				defaultSplitProfile,
				...data.map(item => ({
					value: item.id,
					name: item.name,
					isHighRisk: item.isHighRisk,
					isAvailable: item.isAvailable,
				})),
			],
			onSuccess(response) {
				const availableSplitProfiles = response.filter(
					profile =>
						profile.isAvailable &&
						profile.isHighRisk === isHighRisk,
				);

				if (initialSplitProfile) {
					const initial = response.find(
						profile => profile.value === initialSplitProfile,
					);
					reset({ splitProfileList: initial });
					return;
				}

				if (availableSplitProfiles.length > 1 && isHighRisk) {
					setIsMoreThanTwoHighRiskSplitProfiles?.(true);
				} else {
					setIsMoreThanTwoHighRiskSplitProfiles?.(false);
				}

				if (availableSplitProfiles.length > 1) {
					reset({ splitProfileList: defaultSplitProfile });
				} else {
					const splitProfile =
						availableSplitProfiles?.[0] || defaultSplitProfile;

					reset({ splitProfileList: splitProfile });
				}
			},
			enabled: !!agentId && !isLoadingData,
		},
	);

	const { highRiskCallback } = useContext(HighRiskContext);

	useEffect(() => {
		highRiskCallback.current = isHighRisk => {
			if (!splitProfileItems) return;

			const availableSplitProfiles = splitProfileItems.filter(
				profile =>
					profile.isAvailable && profile.isHighRisk === isHighRisk,
			);

			if (availableSplitProfiles.length === 1) {
				const first = availableSplitProfiles?.[0];
				reset({ splitProfileList: first });
			} else {
				reset({ splitProfileList: defaultSplitProfile });
			}
		};
	}, [highRiskCallback, splitProfileItems, reset]);

	const isHighRiskProfiles = useMemo(() => {
		if (splitProfileItems) {
			const highRiskProfiles = splitProfileItems?.filter(
				item => item.isHighRisk && item.isAvailable,
			).length;

			return highRiskProfiles >= 2;
		}
	}, [splitProfileItems]);

	const isLoading = useMemo(
		() => !agentId || isLoadingData || isFetching,
		[agentId, isLoadingData, isFetching],
	);

	useEffect(() => {
		if (isResetSplitProfilePrevValue) {
			setValue("splitProfileList", defaultSplitProfile);
			setIsResetSplitProfilePrevValue?.(false);
		}
	}, [
		isResetSplitProfilePrevValue,
		setIsResetSplitProfilePrevValue,
		setValue,
	]);

	const isOverridden = useMemo(
		() =>
			isHighRisk &&
			splitProfileList?.value &&
			splitProfileList?.isHighRisk === false,
		[isHighRisk, splitProfileList?.isHighRisk, splitProfileList?.value],
	);

	const isRequiredField = useMemo(() => {
		if (isEditable && isRequired) {
			return !!splitProfileItems?.find(
				item => item.isHighRisk === isHighRisk && item.isAvailable,
			);
		}
	}, [isEditable, isHighRisk, isRequired, splitProfileItems]);

	const handleChange = useCallback(
		value => {
			onChange?.(value, async () => {
				setValue("splitProfileList", prevValue);
				setPrevValue(prevValue);
			});
			setPrevValue(value);
		},
		[onChange, prevValue, setPrevValue, setValue],
	);

	useEffect(() => {
		setHandleSubmit?.(
			() => () =>
				new Promise<any | void>((resolve, reject) => {
					isSuccess ? resolve(handleSubmit) : reject(error);
				}),
		);
	}, [error, handleSubmit, isSuccess, setHandleSubmit]);

	useEffect(() => {
		getHighRiskItem?.(
			isLoading
				? null
				: splitProfileItems?.findLast(item => item.isHighRisk === true)
						?.value,
		);
	}, [getHighRiskItem, isLoading, splitProfileItems]);

	return (
		<>
			<ViewContainer>
				<FormProvider {...methods}>
					<Row>
						<Col sm={12} className={styles.warningContainer}>
							<div>
								{isOverridden && !isLoading && (
									<div className="d-flex mb-3 align-items-center">
										<div className={styles.warningIcon}>
											<AlertIcon />
										</div>
										High risk Split Profile overridden
										{section === "Underwriting" && " by UW"}
									</div>
								)}
								{isHighRisk &&
									isHighRiskProfiles &&
									isEditable &&
									!isLoading &&
									!splitProfileList?.value && (
										<div className="d-flex align-items-center">
											<div className={styles.warningIcon}>
												<AlertIcon />
											</div>
											Agent has multiple high risk
											residual split profiles. Please
											select relevant Split Profile to
											apply
										</div>
									)}
							</div>
						</Col>
						<Col sm={12}>
							<HookSelectFormGroup
								label="Residuals Split Profile"
								options={splitProfileItems}
								getOptionLabel={SplitProfileOption}
								getOptionValue={option => option.value}
								placeholder="Please Select"
								name="splitProfileList"
								items={splitProfileItems}
								isSearchable={false}
								cacheOptions
								defaultOptions
								defaultValue={null}
								debounceTimeout={DebounceTimeout}
								blurLoading={isLoading}
								colLabel={{ sm: 3 }}
								colSelect={{ sm: 8 }}
								containerClassName={styles.selectContainer}
								isDisabled={!isEditable}
								onChange={handleChange}
								rules={{
									validate: isRequiredField && {
										requiredDropDownSplitProfile,
									},
								}}
								showRequiredMark={isRequiredField}
							/>
							<SplitProfileWarningMessage
								splitProfileItems={splitProfileItems}
								isLoading={isLoading}
							/>
						</Col>
					</Row>
				</FormProvider>
			</ViewContainer>
			{splitProfileList?.value && canViewResidualsDetails && (
				<div className={styles.contentContainer}>
					<ViewSplitProfile
						agentId={agentId}
						id={splitProfileList?.value}
					/>
				</div>
			)}
		</>
	);
};
